import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PlatformPeopleService } from '../../../Component/Api/PlatformPeople/PlatformPeopleService';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { PlatformPeopleEmploymentData } from '../../../Component/Model/Dto/Platform/PlatformPeople';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { EmploymentDataTableMapper } from './PeopleEmploymentDataTableColumns';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { toast } from 'react-toastify';
import { WarningDialog } from '../../../Component/Common/Components/WarningDialog';
import { PlatformPersonEmploymentDataModal } from './PersonEmploymentData/PlatformPersonEmploymentDataModal';
import { Button } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { userRolesState } from '../../../State/user.Atom';
import { PlatformPeopleEditRole, UserAdminRole } from '../../../Component/Common/Constants/UserRoles';

export const PlatformPeopleEmploymentDataPage = () => {
  const userRoles = useRecoilValue(userRolesState);
  const [employmentData, setEmploymentData] = useState<PlatformPeopleEmploymentData[]>([]);
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 5 });
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [employmentDataId, setEmploymentDataId] = useState<number>(0);
  const [isConfirmationLoading, setIsConfirmationLoading] = useState<boolean>(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
  const [reportingPeriodToDelete, setReportingPeriodToDelete] = useState<string>('');
  const { platformPeopleId } = useParams<{ platformPeopleId: string }>();
  const [openEmploymentData, setOpenEmploymentDataModal] = useState<boolean>(false);
  const [employmentDataToEdit, setEmploymentDataToEdit] = useState<PlatformPeopleEmploymentData>();
  const editRoleAccess = [
    PlatformPeopleEditRole,
    UserAdminRole  
  ]

  const isEditable = userRoles.some(r => editRoleAccess.includes(r));

  useEffect(() => {
    fetchEmploymentData();
  }, [entriesPerPage,
    pageNumber
  ]);

  const fetchEmploymentData = async () => {
    setIsLoading(true);
    try {
      let employmentResult = await PlatformPeopleService.getEmploymentData(platformPeopleId, pageNumber, entriesPerPage.defaultValue!);
      setEmploymentData(employmentResult.data.results);
      setFilteredItemCount(employmentResult.data.filteredItemsCount);
      setTotalItemsCount(employmentResult.data.totalItemsCount);
    } catch (error) {
      console.log(error);
      setTotalItemsCount(1);
    } finally {
      setIsLoading(false);
    }
  }
  const ClosePlatformPeoplePersonEmploymentDataModal = () => {
    setOpenEmploymentDataModal(false);
    setEmploymentDataToEdit(undefined)
    fetchEmploymentData();
  }
  const openPlatformPeoplePersonEmploymentDataModal = () => {
    setOpenEmploymentDataModal(true)
  }

  const onDeleteEmploymentDataRow = async () => {
    setIsConfirmationLoading(true);
    try {
      await PlatformPeopleService.deleteEmploymentData(platformPeopleId, employmentDataId);
      toast.success('Employment data deleted successfully');
      fetchEmploymentData();
    } catch (error) {
      console.log(error);
    } finally {
      setIsConfirmationLoading(false);
      setShowConfirmationDialog(false);
    }
  };

  const handleEdit = (id: number) => {
    setEmploymentDataToEdit(employmentData.find(x => x.platformPeopleEmploymentDataId === id))
    setOpenEmploymentDataModal(true)
  }

  const handleDelete = (id: number, reportingPeriod: string) => {
    if (totalItemsCount === 1) {
      setShowWarningDialog(true);
    }
    else {
      setEmploymentDataId(id);
      setShowConfirmationDialog(true);
      setReportingPeriodToDelete(reportingPeriod);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmationDialog(false);
    setEmploymentDataId(0);
  };

  const handleCloseWarning = () => {
    setShowWarningDialog(false);
  }

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const employmentTableData = EmploymentDataTableMapper(employmentData, handleEdit, handleDelete);

  return (
    <>
      {isEditable &&
        <Button variant='contained' onClick={() => openPlatformPeoplePersonEmploymentDataModal()}>Capture Employment Data</Button>
      }
      <PlatformPersonEmploymentDataModal key={employmentDataToEdit?.platformPeopleEmploymentDataId} open={openEmploymentData} closeModal={ClosePlatformPeoplePersonEmploymentDataModal} platformPeopleId={platformPeopleId} platformPeopleEmploymentData={employmentDataToEdit} />
      <DataTable
        table={employmentTableData}
        entriesPerPage={entriesPerPage}
        pageNumber={pageNumber}
        filteredItemsCount={filteredItemCount}
        totalItemsCount={totalItemsCount}
        isLoading={isLoading}
        manualSorting={true}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged} />

      <WarningDialog
        showDialog={showWarningDialog}
        heading='Delete Employment Data'
        message='Unfortunately, this is the last employment data record and it cannot be deleted.'
        handleAccept={handleCloseWarning}
      />

      <ConfirmationDialog
        isLoading={isConfirmationLoading}
        showDialog={showConfirmationDialog}
        handleAccept={onDeleteEmploymentDataRow}
        handleCancel={handleCloseConfirmation}
        heading={"Confirm Delete"}
        message={`Are you sure you want to delete this employment data record for period: ${reportingPeriodToDelete}?`}
      />
    </>
  )
}